import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import { Bot, Copy, RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { postFetcher } from '@/hooks'
import { useToast } from '@/hooks/use-toast'
import { useRole } from '@/hooks/auth'
import { Role } from '@/constants/role'

/** token 只显示一次：展示 + 复制 */
export const BotTokenPanel = ({ bot, token }) => {
  const { toast } = useToast()

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token)
      toast({ title: '已复制 token' })
    } catch {
      toast({ title: '复制失败，请手动选择复制', variant: 'destructive' })
    }
  }

  return (
    <div className="space-y-2 rounded-md border bg-muted/40 p-3 text-sm">
      <p>
        <span className="text-muted-foreground">Bot：</span>
        {bot.name} <span className="text-muted-foreground">({bot.id})</span>
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-background px-2 py-1 font-mono text-xs">
          {token}
        </code>
        <Button variant="outline" size="icon" onClick={copy} title="复制">
          <Copy className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-xs text-destructive">
        token 仅显示这一次，作为 bot 的 x-user-id 使用；丢失后只能重新签发。
      </p>
    </div>
  )
}

/** 管理面板「用户管理」下的签发入口，仅 admin 可见 */
const IssueBotDialog = () => {
  const isAdmin = useRole(Role.Admin, true)
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [result, setResult] = useState(null)
  const { trigger, isMutating, error } = useSWRMutation(
    '/users/bots',
    postFetcher,
  )

  if (!isAdmin) return null

  const handleOpenChange = (next) => {
    setOpen(next)
    if (!next) {
      setName('')
      setResult(null)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    const data = await trigger({ name: trimmed })
    setResult(data)
    mutate(['/users'])
  }

  const goConfigure = () => {
    const id = result?.bot?.id
    handleOpenChange(false)
    if (id) navigate(`/admin/bots/${id}`)
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="w-full justify-start"
        onClick={() => setOpen(true)}
      >
        <Bot className="mr-2 h-4 w-4" />
        签发 Bot
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>签发 Bot</DialogTitle>
            <DialogDescription>
              Bot 是无飞书身份的虚拟用户，权限与真人同一套，签发后到用户管理页配置。
            </DialogDescription>
          </DialogHeader>
          {result ? (
            <>
              <BotTokenPanel bot={result.bot} token={result.token} />
              <DialogFooter>
                <Button variant="outline" onClick={() => handleOpenChange(false)}>
                  关闭
                </Button>
                <Button onClick={goConfigure}>去配置权限</Button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                autoFocus
                placeholder="Bot 名称，如 PnL Daily"
                value={name}
                maxLength={255}
                onChange={(e) => setName(e.target.value)}
              />
              {error && (
                <p className="text-sm text-destructive">{error.message}</p>
              )}
              <DialogFooter>
                <Button type="submit" disabled={isMutating || !name.trim()}>
                  {isMutating ? '签发中…' : '签发'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

/** 用户详情页：bot 的重新签发（旧 token 立即失效） */
export const RotateBotToken = ({ bot }) => {
  const isAdmin = useRole(Role.Admin, true)
  const [confirming, setConfirming] = useState(false)
  const [result, setResult] = useState(null)
  const { trigger, isMutating, error } = useSWRMutation(
    `/users/bots/${bot?.id}/rotate`,
    postFetcher,
  )

  if (!isAdmin || bot?.kind !== 'bot') return null

  const handleRotate = async () => {
    setConfirming(false)
    const data = await trigger({})
    setResult(data)
  }

  return (
    <div className="space-y-2">
      <Button
        variant="outline"
        size="sm"
        disabled={isMutating}
        onClick={() => setConfirming(true)}
      >
        <RefreshCw className="mr-2 h-4 w-4" />
        重新签发 token
      </Button>
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>重新签发 {bot.name} 的 token？</DialogTitle>
            <DialogDescription>
              旧 token 会立即失效，正在使用它的脚本需要更新配置。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)}>
              取消
            </Button>
            <Button onClick={handleRotate}>确认签发</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {error && <p className="text-sm text-destructive">{error.message}</p>}
      {result && <BotTokenPanel bot={result.bot} token={result.token} />}
    </div>
  )
}

export default IssueBotDialog
